import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BriefcaseBusiness, CheckCircle, ClipboardCheck, Loader2, Phone, ShieldCheck, Wrench } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { companyInfo, contactInfo } from '../data/mockData';
import { trackEvent, trackEventOnce } from '../utils/analytics';
import { setPageSeo } from '../utils/seo';

const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];
const EXPERIENCE_OPTIONS = [
  ['less-than-1', 'Less than 1 year'],
  ['1-2', '1–2 years'],
  ['3-5', '3–5 years'],
  ['5-plus', '5+ years']
];
const WWCC_OPTIONS = [
  ['yes', 'Yes'],
  ['no', 'No'],
  ['willing', 'No, but willing to obtain'],
  ['unsure', 'Unsure / depends on state']
];
const BRAND_OPTIONS = ['Bambu Lab', 'Prusa', 'Creality', 'Anycubic', 'FlashForge', 'Elegoo', 'Qidi Tech', 'Raise3D', 'Ultimaker', 'Formlabs', 'MakerBot', 'Snapmaker', 'Voron / custom printers', 'Other'];
const WORK_TYPE_OPTIONS = ['Consumer/home printer repairs', 'School printer servicing', 'Business/commercial repairs', 'Preventative maintenance', 'Fleet servicing'];
const TECHNICIAN_BENEFITS = [
  ['Earn consistent work in your area', 'Be considered for repair and maintenance jobs that match your location, availability and technical strengths.'],
  ['Supported by a specialist team', 'Work with a 3D printer team backed by over 12+ years of hands-on industry experience.'],
  ['Spare parts provided', 'For approved service work, the required spare parts can be supplied through 3D Printer Gear so the job is set up properly.'],
  ['Training provided', 'We help suitable technicians understand our service standards, reporting process and customer expectations before representing us.'],
  ['Customer liaison handled', '3D Printer Gear stays across the customer relationship, expectations and follow-up so you can focus on the technical work.'],
  ['Represent a specialist brand', 'Join a repair network attached to an established 3D printer business with strong search visibility and customer trust.']
];
const NETWORK_PROOF = [
  ['700+', 'Google reviews'],
  ['12+', 'Years in 3D printing'],
  ['AU-wide', 'Repair enquiries'],
  ['Schools + business', 'Service demand']
];
const PROCESS_STEPS = [
  ['Apply', 'Send the quick expression of interest below.'],
  ['Vetting call', 'If it looks aligned, we call to understand experience, communication style and availability.'],
  ['Contractor fit', 'Suitable technicians can be considered for jobs by location, capability and customer needs.']
];
const POST_SUBMIT_STEPS = [
  ['Application review', 'We check your location, experience, brands, tools and vital requirements.'],
  ['Phone conversation', 'If it looks like a potential fit, we call you to understand how you work and whether we can work well together.'],
  ['Details later', 'If the conversation goes well, we can request onboarding details such as documents, service areas and contractor expectations.'],
  ['Future job fit', 'Approved contractors may be considered for suitable repair or maintenance jobs through 3D Printer Gear.']
];

const defaultFormData = {
  fullName: '', phone: '', email: '', businessName: '', state: '', suburb: '',
  hasAbn: '', hasTools: '', hasPublicLiability: '', hasLicenceAndVehicle: '',
  wwccStatus: '', yearsExperience: '', brands: [], workTypes: [], experienceSummary: '', consent: true
};

const kebab = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const BooleanQuestion = ({ id, label, value, onChange }) => (
  <div className="border border-gray-200 bg-white p-4 rounded-sm" data-testid={`${id}-wrap`}>
    <Label className="text-sm font-extrabold text-gray-900 mb-3 block" data-testid={`${id}-label`}>{label}</Label>
    <div className="grid grid-cols-2 gap-2">
      {['yes', 'no'].map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option === 'yes')}
          className={`border px-4 py-2.5 rounded-sm text-sm font-extrabold transition-colors ${value === (option === 'yes') ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-900'}`}
          data-testid={`${id}-${option}-button`}
        >
          {option === 'yes' ? 'Yes' : 'No'}
        </button>
      ))}
    </div>
  </div>
);

const CheckboxGroup = ({ label, options, values, onToggle, testId }) => (
  <div className="space-y-3" data-testid={`${testId}-group`}>
    <Label className="text-sm font-extrabold text-gray-900" data-testid={`${testId}-label`}>{label}</Label>
    <div className="grid sm:grid-cols-2 gap-2">
      {options.map((option) => {
        const optionId = kebab(option);
        return (
          <label key={option} className="flex items-start gap-2 border border-gray-200 bg-white hover:border-gray-900 p-3 rounded-sm cursor-pointer transition-colors" data-testid={`${testId}-${optionId}-option`}>
            <Checkbox checked={values.includes(option)} onCheckedChange={() => onToggle(option)} data-testid={`${testId}-${optionId}-checkbox`} />
            <span className="text-sm text-gray-700" data-testid={`${testId}-${optionId}-text`}>{option}</span>
          </label>
        );
      })}
    </div>
  </div>
);

export default function TechnicianApplicationPage() {
  const [formData, setFormData] = useState(defaultFormData);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [applicationId, setApplicationId] = useState('');

  useEffect(() => {
    setPageSeo({
      title: 'Apply as a 3D Printer Technician | 3D Printer Gear Contractor Network',
      description: 'Apply to join the 3D Printer Gear contractor technician network for qualified 3D printer repair, maintenance, school and business service opportunities across Australia.',
      canonical: 'https://repair.3dprintergear.com.au/apply-as-technician',
      image: companyInfo.logo
    });
    trackEventOnce('technician_application_page_view', 'technician_application_page_view', { page_location: 'apply_as_technician' });
  }, []);

  const selectedBrands = useMemo(() => formData.brands.length, [formData.brands]);

  const updateField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
    setError('');
  };

  const toggleListValue = (field, value) => {
    setFormData((current) => ({
      ...current,
      [field]: current[field].includes(value)
        ? current[field].filter((item) => item !== value)
        : [...current[field], value]
    }));
    setError('');
  };

  const validate = () => {
    const requiredFields = ['fullName', 'phone', 'email', 'state', 'suburb', 'wwccStatus', 'yearsExperience', 'experienceSummary'];
    const missingField = requiredFields.find((field) => !String(formData[field] || '').trim());
    if (missingField) return 'Please complete all required contact, location and experience fields.';
    if ([formData.hasAbn, formData.hasTools, formData.hasPublicLiability, formData.hasLicenceAndVehicle].some((value) => value === '')) return 'Please answer each vital requirement question.';
    if (!formData.brands.length) return 'Please select at least one printer brand.';
    if (!formData.workTypes.length) return 'Please select at least one type of work.';
    if (!formData.consent) return 'Please confirm we can contact you about your application.';
    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationMessage = validate();
    if (validationMessage) {
      setError(validationMessage);
      trackEvent('technician_application_submit_error', { error_type: 'client_validation' });
      return;
    }

    setStatus('submitting');
    setError('');
    trackEvent('technician_application_submit_attempt', { selected_brands: selectedBrands });

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/technician-application`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || 'Unable to submit your application.');
      setApplicationId(payload.application_id || '');
      setStatus('submitted');
      trackEvent('technician_application_submit_success', { selected_brands: selectedBrands });
    } catch (submitError) {
      setError(submitError.message || 'Unable to submit your application. Please try again.');
      setStatus('idle');
      trackEvent('technician_application_submit_error', { error_type: 'api_or_network' });
    }
  };

  if (status === 'submitted') {
    return (
      <div className="min-h-screen bg-white" data-testid="technician-application-success-page">
        <main className="mx-auto max-w-3xl px-4 sm:px-6 py-16">
          <div className="border border-gray-200 bg-white p-8 text-center rounded-sm" data-testid="technician-application-success-card">
            <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" data-testid="technician-application-success-icon" />
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900 mb-3" data-testid="technician-application-success-heading">Thanks — your application has been received</h1>
            <p className="text-base text-gray-600 leading-relaxed mb-5" data-testid="technician-application-success-message">Our team reviews each technician enquiry manually. If it looks like a fit, we’ll call you to discuss experience, service areas and next steps.</p>
            {applicationId && <p className="text-xs text-gray-500 mb-6" data-testid="technician-application-reference">Reference: {applicationId}</p>}
            <div className="border border-gray-200 bg-gray-50 text-left p-5 rounded-sm mb-6" data-testid="technician-application-next-steps">
              <h2 className="text-lg font-extrabold text-gray-900 mb-4" data-testid="technician-application-next-steps-heading">What happens next?</h2>
              <div className="grid gap-3">
                {POST_SUBMIT_STEPS.map(([title, copy], index) => (
                  <div key={title} className="grid grid-cols-[32px_1fr] gap-3 items-start" data-testid={`technician-application-next-step-${kebab(title)}`}>
                    <div className="h-8 w-8 bg-gray-900 text-white flex items-center justify-center text-sm font-extrabold rounded-sm" data-testid={`technician-application-next-step-${kebab(title)}-number`}>{index + 1}</div>
                    <div>
                      <h3 className="text-sm font-extrabold text-gray-900" data-testid={`technician-application-next-step-${kebab(title)}-title`}>{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed" data-testid={`technician-application-next-step-${kebab(title)}-copy`}>{copy}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <Link to="/" className="inline-flex bg-gray-900 hover:bg-black text-white text-sm font-extrabold px-5 py-3 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors" data-testid="technician-application-success-home-link">Back to repair site</Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="technician-application-page">
      <header className="border-b border-gray-200 bg-white" data-testid="technician-application-header">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" data-testid="technician-application-logo-link"><img src={companyInfo.logo} alt={companyInfo.name} className="h-10 w-auto" data-testid="technician-application-logo" /></Link>
          <a href="tel:1300334327" className="hidden sm:inline-flex items-center gap-2 text-sm font-extrabold text-gray-900" data-testid="technician-application-phone-link"><Phone className="h-4 w-4" />{contactInfo.phone} - Option 2</a>
        </div>
      </header>

      <main>
        <section className="py-12 sm:py-16 bg-white" data-testid="technician-application-hero">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5 space-y-7">
              <div data-testid="technician-application-hero-copy">
                <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-gray-500 mb-4" data-testid="technician-application-eyebrow">Contractor technician network</p>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900 leading-none mb-5" data-testid="technician-application-heading">Get considered for 3D printer repair work in your area</h1>
                <p className="text-base text-gray-600 leading-relaxed mb-5" data-testid="technician-application-summary">3D Printer Gear receives repair, maintenance, school and business service enquiries across Australia. When we can’t attend directly, we want trusted contractors we can call on to represent our service standard.</p>
                <p className="text-base text-gray-700 leading-relaxed font-semibold" data-testid="technician-application-value-prop">If you’re experienced, well-equipped and good with customers, this is a simple first step to be considered for future contractor work.</p>
              </div>

              <div className="grid grid-cols-2 gap-px bg-gray-200 border border-gray-200" data-testid="technician-network-proof-grid">
                {NETWORK_PROOF.map(([number, label]) => (
                  <div key={label} className="bg-white p-4" data-testid={`technician-network-proof-${kebab(label)}`}>
                    <p className="text-2xl font-extrabold text-gray-900 leading-none" data-testid={`technician-network-proof-${kebab(label)}-number`}>{number}</p>
                    <p className="text-xs text-gray-500 mt-1" data-testid={`technician-network-proof-${kebab(label)}-label`}>{label}</p>
                  </div>
                ))}
              </div>

              <div className="space-y-3" data-testid="technician-benefits-section">
                <h2 className="text-xl font-extrabold tracking-tight text-gray-900" data-testid="technician-benefits-heading">Why apply?</h2>
                <div className="grid gap-3">
                  {TECHNICIAN_BENEFITS.map(([title, copy]) => (
                    <article key={title} className="border border-gray-200 bg-gray-50 p-4 rounded-sm" data-testid={`technician-benefit-${kebab(title)}`}>
                      <div className="flex items-start gap-3">
                        <BriefcaseBusiness className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <h3 className="text-sm font-extrabold text-gray-900 mb-1" data-testid={`technician-benefit-${kebab(title)}-title`}>{title}</h3>
                          <p className="text-sm text-gray-600 leading-relaxed" data-testid={`technician-benefit-${kebab(title)}-copy`}>{copy}</p>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>

              <div className="bg-gray-900 text-white p-5 rounded-sm" data-testid="technician-who-we-are-section">
                <div className="flex items-start gap-3 mb-4">
                  <ShieldCheck className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h2 className="text-xl font-extrabold tracking-tight mb-2" data-testid="technician-who-we-are-heading">Who is 3D Printer Gear?</h2>
                    <p className="text-sm text-gray-300 leading-relaxed" data-testid="technician-who-we-are-copy">We’re a specialist Australian 3D printer business with a dedicated service and repair division, strong Google visibility, hundreds of customer reviews and growing demand from hobbyists, schools and businesses.</p>
                  </div>
                </div>
                <div className="grid gap-px bg-gray-700 border border-gray-700" data-testid="technician-process-grid">
                  {PROCESS_STEPS.map(([step, copy], index) => (
                    <div key={step} className="bg-gray-900 p-3" data-testid={`technician-process-step-${kebab(step)}`}>
                      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-orange-400 mb-1" data-testid={`technician-process-step-${kebab(step)}-number`}>Step {index + 1}</p>
                      <h3 className="text-sm font-extrabold text-white" data-testid={`technician-process-step-${kebab(step)}-title`}>{step}</h3>
                      <p className="text-xs text-gray-400 leading-relaxed mt-1" data-testid={`technician-process-step-${kebab(step)}-copy`}>{copy}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-l-4 border-orange-500 pl-4" data-testid="technician-fit-note">
                <div className="flex items-start gap-2">
                  <ClipboardCheck className="h-5 w-5 text-gray-900 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700 leading-relaxed" data-testid="technician-fit-note-copy"><strong>We’re selective because you would be representing us.</strong> This short form helps us decide who is worth speaking with before any onboarding details are requested.</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="lg:col-span-7 border border-gray-200 bg-white rounded-sm overflow-hidden" data-testid="technician-application-form">
              <div className="h-2 bg-orange-500" data-testid="technician-application-form-accent" />
              <div className="bg-gray-900 text-white px-5 py-5 sm:px-6" data-testid="technician-application-form-header">
                <p className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-orange-400 mb-1" data-testid="technician-application-form-eyebrow">Apply as a Technician</p>
                <h2 className="text-xl font-extrabold" data-testid="technician-application-form-title">Register your interest</h2>
                <p className="text-xs text-gray-300 mt-1" data-testid="technician-application-form-subtitle">A short first step so we can decide who to call for a proper chat.</p>
              </div>

              <div className="p-5 sm:p-6 space-y-7">
                {error && <div className="border border-red-200 bg-red-50 text-red-700 text-sm font-semibold px-3 py-2 rounded-sm" data-testid="technician-application-error">{error}</div>}

                <section className="space-y-4" data-testid="technician-contact-section">
                  <h3 className="text-base font-extrabold text-gray-900" data-testid="technician-contact-heading">Contact details</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label htmlFor="technician-full-name">Full name *</Label><Input id="technician-full-name" value={formData.fullName} onChange={(e) => updateField('fullName', e.target.value)} data-testid="technician-full-name-input" /></div>
                    <div className="space-y-1.5"><Label htmlFor="technician-phone">Phone *</Label><Input id="technician-phone" value={formData.phone} onChange={(e) => updateField('phone', e.target.value)} data-testid="technician-phone-input" /></div>
                    <div className="space-y-1.5"><Label htmlFor="technician-email">Email *</Label><Input id="technician-email" type="email" value={formData.email} onChange={(e) => updateField('email', e.target.value)} data-testid="technician-email-input" /></div>
                    <div className="space-y-1.5"><Label htmlFor="technician-business">Business name</Label><Input id="technician-business" value={formData.businessName} onChange={(e) => updateField('businessName', e.target.value)} data-testid="technician-business-name-input" /></div>
                    <div className="space-y-1.5"><Label htmlFor="technician-state">State *</Label><Select value={formData.state} onValueChange={(value) => updateField('state', value)}><SelectTrigger id="technician-state" data-testid="technician-state-select"><SelectValue placeholder="Select state" /></SelectTrigger><SelectContent>{AU_STATES.map((state) => <SelectItem key={state} value={state} data-testid={`technician-state-option-${state.toLowerCase()}`}>{state}</SelectItem>)}</SelectContent></Select></div>
                    <div className="space-y-1.5"><Label htmlFor="technician-suburb">Main suburb/city *</Label><Input id="technician-suburb" value={formData.suburb} onChange={(e) => updateField('suburb', e.target.value)} data-testid="technician-suburb-input" /></div>
                  </div>
                </section>

                <section className="space-y-4" data-testid="technician-vitals-section">
                  <h3 className="text-base font-extrabold text-gray-900" data-testid="technician-vitals-heading">Vitals</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <BooleanQuestion id="technician-abn" label="Do you have an active ABN? *" value={formData.hasAbn} onChange={(value) => updateField('hasAbn', value)} />
                    <BooleanQuestion id="technician-tools" label="Do you have your own repair tools? *" value={formData.hasTools} onChange={(value) => updateField('hasTools', value)} />
                    <BooleanQuestion id="technician-insurance" label="Do you have public liability insurance? *" value={formData.hasPublicLiability} onChange={(value) => updateField('hasPublicLiability', value)} />
                    <BooleanQuestion id="technician-licence" label="Do you have a driver’s licence and vehicle? *" value={formData.hasLicenceAndVehicle} onChange={(value) => updateField('hasLicenceAndVehicle', value)} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label htmlFor="technician-wwcc">Working With Children Check *</Label><Select value={formData.wwccStatus} onValueChange={(value) => updateField('wwccStatus', value)}><SelectTrigger id="technician-wwcc" data-testid="technician-wwcc-select"><SelectValue placeholder="Select status" /></SelectTrigger><SelectContent>{WWCC_OPTIONS.map(([value, label]) => <SelectItem key={value} value={value} data-testid={`technician-wwcc-option-${value}`}>{label}</SelectItem>)}</SelectContent></Select></div>
                    <div className="space-y-1.5"><Label htmlFor="technician-experience">Years repairing 3D printers *</Label><Select value={formData.yearsExperience} onValueChange={(value) => updateField('yearsExperience', value)}><SelectTrigger id="technician-experience" data-testid="technician-experience-select"><SelectValue placeholder="Select experience" /></SelectTrigger><SelectContent>{EXPERIENCE_OPTIONS.map(([value, label]) => <SelectItem key={value} value={value} data-testid={`technician-experience-option-${value}`}>{label}</SelectItem>)}</SelectContent></Select></div>
                  </div>
                </section>

                <section className="space-y-5" data-testid="technician-experience-section">
                  <CheckboxGroup label="Which 3D printer brands are you familiar with? *" options={BRAND_OPTIONS} values={formData.brands} onToggle={(value) => toggleListValue('brands', value)} testId="technician-brands" />
                  <CheckboxGroup label="What kind of work are you interested in? *" options={WORK_TYPE_OPTIONS} values={formData.workTypes} onToggle={(value) => toggleListValue('workTypes', value)} testId="technician-work-types" />
                  <div className="space-y-1.5"><Label htmlFor="technician-summary">Briefly tell us about your 3D printer repair experience *</Label><Textarea id="technician-summary" rows={4} value={formData.experienceSummary} onChange={(e) => updateField('experienceSummary', e.target.value)} placeholder="Brands, common repairs, customers you have helped, or what you are strongest at..." data-testid="technician-experience-summary-input" /></div>
                </section>

                <label className="flex items-start gap-3 text-sm text-gray-600" data-testid="technician-consent-wrap">
                  <Checkbox checked={formData.consent} onCheckedChange={(value) => updateField('consent', Boolean(value))} data-testid="technician-consent-checkbox" />
                  <span data-testid="technician-consent-text">I agree that 3D Printer Gear can contact me about this technician application.</span>
                </label>

                <Button type="submit" disabled={status === 'submitting'} className="w-full bg-gray-900 hover:bg-black text-white text-sm font-extrabold px-5 py-3 rounded-sm border-b-4 border-orange-500 hover:border-orange-400 transition-colors disabled:opacity-70" data-testid="technician-application-submit-button">
                  {status === 'submitting' ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Submitting application</> : <><Wrench className="h-4 w-4 mr-2" /> Apply as a Technician</>}
                </Button>
              </div>
            </form>
          </div>
        </section>
      </main>
    </div>
  );
}